import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ACTIVO, CARGANDO, ERROR, INACTIVO } from '../constantes';
import Usuario, { Correo, Telefono } from '@/models/store/usuario.model';
import Cookies from 'js-cookie';
import api from '@/providers/api';
import { ESTADO_ENROLAMIENTO, OBTENER_PERFIL } from '@/helpers/urls';
import { mostrarAlertaError } from '@/helpers/alert';
import RespuestaApi from '@/models/respuestas/resApi.model';
import {
  EstadoEnrolamiento,
  PerfilUsuario,
} from '@/models/respuestas/usuario.model';
import { zonedTimeToUtc } from 'date-fns-tz';
import { timeZone } from '@/helpers/date';
import { addHours, differenceInMilliseconds, formatISO } from 'date-fns';
import { manejarErrorAlerta } from '@/helpers/errores';
import { Domicilio } from '@/models/respuestas/domicilio.model';

export const estadoInicialUsuario: Usuario = {
  id: null,
  rut: null,
  nombre: null,
  idRol: null,
  rol: null,
  correo: null,
  verificado: null,
  enrolado: null,
  fechaDesbloqueo: null,
  menu: null,
  domicilios: null,
  correos: null,
  telefonos: null,
};
const horasDesbloqueo = 1;

export const CLIENTE = 1;
export const EJECUTIVO_ADMIN = 2;
export const EJECUTIVO_VISOR = 3;

export const CELULAR = 1;
export const TELEFONO_FIJO = 2;

export const obtenerIsoFechaBloqueo = (fecha: string): string => {
  // PASAR FECHA DE BLOQUEO NO DESBLOQUEO
  const fechaUtc: Date = zonedTimeToUtc(fecha, timeZone);

  // Guardo la fecha de bloqueo + 1 hora para calcular desbloqueo
  const fechaIso = formatISO(addHours(fechaUtc, horasDesbloqueo));

  return fechaIso;
};

export const obtenerEstadoEnrolamientoQuery = async (_rut: string) => {
  try {
    const {
      data: {
        resultado: { enrolado, fechaBloqueo },
      },
    } = await api.post<RespuestaApi<EstadoEnrolamiento>>(ESTADO_ENROLAMIENTO);

    let fechaDesbloqueo: string = null;

    if (fechaBloqueo) {
      const fechaBloqueoIso = obtenerIsoFechaBloqueo(fechaBloqueo);
      const diferencia = differenceInMilliseconds(
        new Date(fechaBloqueoIso),
        new Date()
      );

      fechaDesbloqueo = diferencia > 0 ? fechaBloqueoIso : null;
    }

    return { fechaDesbloqueo, enrolado };
  } catch {
    mostrarAlertaError('Hubo un error al obtener el estado del SII');
  }
};

export const obtenerEstadoEnrolamiento = createAsyncThunk(
  'usuario/estadoEnrolamiento',
  async (rut: string) => {
    return await obtenerEstadoEnrolamientoQuery(rut);
  }
);

export const obtenerInformacionDelPerfil = createAsyncThunk(
  'usuario/datos-perfil',
  async (_, thunk) => {
    try {
      const {
        data: {
          resultado: { correos, direcciones, telefonos },
        },
      } = await api.get<RespuestaApi<PerfilUsuario>>(OBTENER_PERFIL);

      return {
        domicilios: direcciones,
        telefonos,
        correos,
      };
    } catch (error) {
      manejarErrorAlerta(
        error,
        'Hubo un error al obtener los datos del perfil'
      );
      thunk.rejectWithValue(error);
      throw error;
    }
  }
);

const usuarioSlice = createSlice({
  name: 'usuario',
  initialState: { ...estadoInicialUsuario, estado: INACTIVO },
  reducers: {
    login: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    actualizarEstadoEnrolamiento: (state, { payload }) => {
      state.enrolado = payload;
    },
    agregarTelefono: ({ telefonos }, { payload }: PayloadAction<Telefono>) => {
      if (payload.telefonoPrincipal) {
        // Si existe modifico el index 0, porque la principal siempre va a ser la primera de la lista
        if (telefonos[0]?.telefonoPrincipal) {
          telefonos[0].telefonoPrincipal = false;
        }
        telefonos.unshift(payload);
      } else {
        telefonos.push(payload);
      }
    },

    modificarTelefono: (
      { telefonos },
      { payload }: PayloadAction<Telefono>
    ) => {
      const indexTelefono = telefonos.findIndex(
        (d) => d.idTelefono === payload.idTelefono
      );

      // Si edito la cuenta a principal
      if (payload.telefonoPrincipal) {
        // Modifico el index 0, porque la principal siempre va a ser la primera de la lista
        telefonos[0] = {
          ...telefonos[0],
          telefonoPrincipal: false,
        };

        // Elimino su posicion antigua
        telefonos.splice(indexTelefono, 1);

        // Lo pongo al principio de la lista porque es principal
        telefonos.unshift(payload);
      } else {
        telefonos[indexTelefono] = { ...payload };
      }
    },

    eliminarTelefono: (
      state,
      { payload }: PayloadAction<Telefono['idTelefono']>
    ) => {
      const nuevoEstado = state.telefonos?.filter(
        (t) => t.idTelefono !== payload
      );
      state.telefonos = nuevoEstado;
    },

    agregarDomicilio: (
      { domicilios },
      { payload }: PayloadAction<Domicilio>
    ) => {
      if (payload.direccionPrincipal) {
        // Si existe modifico el index 0, porque la principal siempre va a ser la primera de la lista
        if (domicilios[0]?.direccionPrincipal) {
          domicilios[0].direccionPrincipal = false;
        }
        domicilios.unshift(payload);
      } else {
        domicilios.push(payload);
      }
    },

    modificarDomicilio: (
      { domicilios },
      { payload }: PayloadAction<Domicilio>
    ) => {
      const indexDomicilio = domicilios.findIndex(
        (d) => d.idDireccion === payload.idDireccion
      );

      // Si edito la cuenta a principal
      if (payload.direccionPrincipal) {
        // Modifico el index 0, porque la principal siempre va a ser la primera de la lista
        domicilios[0] = {
          ...domicilios[0],
          direccionPrincipal: false,
        };

        // Elimino su posicion antigua
        domicilios.splice(indexDomicilio, 1);

        // Lo pongo al principio de la lista porque es principal
        domicilios.unshift(payload);
      } else {
        domicilios[indexDomicilio] = { ...payload };
      }
    },

    eliminarDomicilio: (
      state,
      { payload }: PayloadAction<Domicilio['idDireccion']>
    ) => {
      const nuevoEstado = state.domicilios?.filter(
        (d) => d.idDireccion !== payload
      );
      state.domicilios = nuevoEstado;
    },

    agregarCorreo: ({ correos }, { payload }: PayloadAction<Correo>) => {
      if (payload.correoPrincipal) {
        // Si existe modifico el index 0, porque la principal siempre va a ser la primera de la lista
        if (correos[0]?.correoPrincipal) {
          correos[0].correoPrincipal = false;
        }
        correos.unshift(payload);
      } else {
        correos.push(payload);
      }
    },

    modificarCorreo: ({ correos }, { payload }: PayloadAction<Correo>) => {
      const indexCorreo = correos.findIndex(
        (c) => c.idCorreo === payload.idCorreo
      );

      // Si edito la cuenta a principal
      if (payload.correoPrincipal) {
        // Modifico el index 0, porque la principal siempre va a ser la primera de la lista
        correos[0] = {
          ...correos[0],
          correoPrincipal: false,
        };

        // Elimino su posicion antigua
        correos.splice(indexCorreo, 1);

        // Lo pongo al principio de la lista porque es principal
        correos.unshift(payload);
      } else {
        correos[indexCorreo] = { ...payload };
      }
    },

    eliminarCorreo: (state, { payload }: PayloadAction<Correo['idCorreo']>) => {
      const nuevoEstado = state.correos?.filter((d) => d.idCorreo !== payload);
      state.correos = nuevoEstado;
    },

    establecerBloqueoEnrolamiento: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      if (payload.length === 19) {
        // Largo de fecha formato progreso
        state.fechaDesbloqueo = obtenerIsoFechaBloqueo(payload);
      } else {
        state.fechaDesbloqueo = null;
      }
    },
    cerrarSesion: () => {
      Cookies.remove('t');
      return { ...estadoInicialUsuario, estado: INACTIVO };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(obtenerEstadoEnrolamiento.pending, (state) => {
        state.estado = CARGANDO;
      })
      .addCase(obtenerEstadoEnrolamiento.fulfilled, (state, { payload }) => {
        return {
          ...state,
          ...payload,
          estado: ACTIVO,
        };
      });

    builder
      .addCase(obtenerInformacionDelPerfil.pending, (state) => {
        return {
          ...state,
          estado: CARGANDO,
        };
      })
      .addCase(obtenerInformacionDelPerfil.rejected, (state) => {
        return { ...state, estado: ERROR };
      })
      .addCase(obtenerInformacionDelPerfil.fulfilled, (state, { payload }) => {
        return {
          ...state,
          ...payload,
          estado: ACTIVO,
        };
      });
  },
});

export const {
  login,
  cerrarSesion,
  actualizarEstadoEnrolamiento,
  establecerBloqueoEnrolamiento,
  agregarCorreo,
  agregarDomicilio,
  agregarTelefono,
  modificarCorreo,
  modificarDomicilio,
  modificarTelefono,
  eliminarCorreo,
  eliminarDomicilio,
  eliminarTelefono,
} = usuarioSlice.actions;
export default usuarioSlice.reducer;
